import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Video, MessageCircle, FileText, Bell, X, Clock,
  ArrowRight, Layers, CreditCard, BarChart3, MessageSquare, Reply,
  UserPlus, Phone, Mail, Award, RotateCcw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../utils/auth';
import {
  trackNotification, remindMeLater, isStaffUser, isStaffOnlyNotification, shouldShowInApp,
} from '../../utils/notificationManager';
import { playNotificationSound } from '../../utils/notificationSound';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); InAppNotification.scss deleted.
//
// 🔴 THE FINDING: this component stylesheet declared EIGHT global tokens on
// `:root` — `--wa-green`, `--wa-bubble-bg`, `--wa-card-bg`, `--wa-meta-text`,
// `--wa-name-text` and friends — plus a `:root[data-theme="dark"]` fork for
// them. A second copy of the WhatsApp palette, living in the one file nobody
// would look in, and reachable from every other stylesheet in the app for
// exactly as long as this component happened to be mounted. Six of the eight
// were byte-identical to tokens `_brand-external.scss` already held.
//
// Nothing outside this sheet read them (checked), so they are deleted rather
// than migrated. The three roles that were genuinely missing were added there
// instead: `--ext-whatsapp-green-dark`, `--wa-client-card`, `--wa-client-action`.
//
// ⚠️ `--wa-bubble-bg` was `#dcf8c6` where `--ext-whatsapp-bubble-out` is
// `#d9fdd3` — two of WhatsApp's own bubble greens, one current and one from an
// older client. Converged onto `--wa-client-out`, so a grep for either has one
// answer.
//
// FIVE dark-mode forks disappear without being ported, because each was
// restating a token that already flips: the toast, modal and enquiry-card
// shadows are `--popover-shadow` (one `tw-shadow-popover`), the modal scrim is
// `--overlay-bg`, and the neutral banner's icon wash is `--elevate-2`. Only the
// class-started banner keeps a `dark:` variant, and it earns it — that gradient
// gets DEEPER in dark while `--primary` itself gets lighter.
//
// Four keyframes were dropped in favour of ones the config already had:
// `inapp-toast-progress` (width 100%→0) is `countdown` (scaleX, declared with no
// duration precisely so the component can set `animationDuration` inline — which
// all three progress bars here do), `inapp-modal-fade-in` is `panel-fade`,
// `inapp-modal-scale-in` is `dialog-in`, and `inapp-toast-slide-in` is
// `toast-slide-in`. Three were genuinely new and are in the config now:
// `toast-slide-out` (the pair `toast-slide-in` never had), `banner-down` and
// `banner-up`.
//
// ⚠️ The banner's action buttons were `rgba(255,255,255,0.2)` for EVERY banner
// type, including the three that paint a light card. A white-20% wash on white
// is an invisible button, and the same rule set `color: inherit` over it. The
// two families are now two strings, and the neutral one gets `--elevate-2`.

// ── Top banner ──
const BANNER =
  'tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-toast tw-flex tw-min-h-[52px] tw-items-center ' +
  'tw-justify-between tw-gap-4 tw-px-5 tw-py-2.5 ' +
  'max-[480px]:tw-gap-2.5 max-[480px]:tw-px-3 max-[480px]:tw-py-2';
// The brand gradient runs deep→bright in light and deeper still in dark: on a
// dark page a banner painted at the brand's own lightness stops reading as an
// interruption. This is the one dark: variant in the file.
const BANNER_BRAND =
  BANNER + ' tw-text-on-status ' +
  'tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--primary))_72%,black),hsl(var(--primary)))] ' +
  'dark:tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--primary))_42%,black),color-mix(in_srgb,hsl(var(--primary))_68%,black))]';
const BANNER_NEUTRAL =
  BANNER + ' tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-raised ' +
  'tw-text-content tw-shadow-[0_2px_8px_var(--card-shadow)]';
const BANNER_CONTENT = 'tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3';
const BANNER_ICON =
  'tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md';
const BANNER_ICON_BRAND = BANNER_ICON + ' tw-bg-on-status/15';
const BANNER_ICON_NEUTRAL = BANNER_ICON + ' tw-bg-[var(--elevate-2)] tw-text-content';
const BANNER_TEXT = 'tw-flex tw-min-w-0 tw-flex-col';
const BANNER_TITLE = 'tw-truncate tw-text-[0.88rem] tw-font-medium';
// Hidden on a phone: the title and the CTA are what the bar is for, and the
// second line is what has to give when there are 320 pixels to spend.
const BANNER_MESSAGE = 'tw-truncate tw-text-[0.8rem] tw-opacity-85 max-[480px]:tw-hidden';
const BANNER_ACTIONS = 'tw-flex tw-shrink-0 tw-items-center tw-gap-2';
const BANNER_CTA =
  'tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-sm ' +
  'tw-border-none tw-px-4 tw-py-[7px] tw-text-[0.82rem] tw-font-semibold tw-text-inherit ' +
  'tw-transition-colors tw-duration-150';
const BANNER_CTA_BRAND = BANNER_CTA + ' tw-bg-on-status/20 hover:tw-bg-on-status/30';
const BANNER_CTA_NEUTRAL =
  BANNER_CTA + ' tw-bg-[var(--elevate-2)] hover:tw-bg-surface-hover';
const BANNER_REMIND =
  'tw-flex tw-cursor-pointer tw-items-center tw-gap-[5px] tw-whitespace-nowrap tw-rounded-sm ' +
  'tw-border tw-border-solid tw-bg-transparent tw-px-3 tw-py-[7px] tw-text-[0.78rem] ' +
  'tw-font-medium tw-text-inherit tw-opacity-80 tw-transition-all tw-duration-150 ' +
  'hover:tw-opacity-100';
const BANNER_REMIND_BRAND =
  BANNER_REMIND + ' tw-border-on-status/25 hover:tw-bg-on-status/10';
const BANNER_REMIND_NEUTRAL =
  BANNER_REMIND + ' tw-border-line hover:tw-bg-surface-hover';
const BANNER_REMIND_LABEL = 'max-[480px]:tw-hidden';
const BANNER_CLOSE =
  'tw-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[6px] ' +
  'tw-border-none tw-bg-transparent tw-text-inherit tw-opacity-60 tw-transition-all ' +
  'tw-duration-150 hover:tw-opacity-100';
const BANNER_CLOSE_BRAND = BANNER_CLOSE + ' hover:tw-bg-on-status/15';
const BANNER_CLOSE_NEUTRAL = BANNER_CLOSE + ' hover:tw-bg-surface-hover';

// Every banner type except class_started paints a light card, so its inks and
// washes are the neutral set. class_started is the only one DISPLAY_MODE sends
// here, but `notification.displayMode` can override that for any type.
const isBrandBanner = (type) => type === 'class_started';

// ── Toast stack ──
const TOAST_CONTAINER =
  'tw-pointer-events-none tw-fixed tw-bottom-5 tw-right-5 tw-z-toast tw-flex tw-w-full ' +
  'tw-max-w-[360px] tw-flex-col-reverse tw-gap-2.5 ' +
  'max-[480px]:tw-bottom-2 max-[480px]:tw-left-2 max-[480px]:tw-right-2 ' +
  'max-[480px]:tw-max-w-[calc(100vw-16px)]';

// ── Ordinary toast ──
const TOAST =
  'tw-group tw-pointer-events-auto tw-relative tw-overflow-hidden tw-rounded-md tw-border ' +
  'tw-border-solid tw-border-line tw-bg-surface-raised tw-shadow-popover';
const TOAST_CLOSE =
  'tw-absolute tw-right-2 tw-top-2 tw-z-[2] tw-flex tw-h-[22px] tw-w-[22px] tw-cursor-pointer ' +
  'tw-items-center tw-justify-center tw-rounded-[4px] tw-border-none tw-bg-transparent ' +
  'tw-text-content-subtle tw-opacity-0 tw-transition-all tw-duration-150 ' +
  'group-hover:tw-opacity-100 hover:tw-bg-surface-hover hover:tw-text-content';
const TOAST_BODY = 'tw-flex tw-items-start tw-gap-2.5 tw-px-3.5 tw-py-3';
const TOAST_ICON =
  'tw-flex tw-h-[34px] tw-w-[34px] tw-shrink-0 tw-items-center tw-justify-center ' +
  'tw-rounded-sm tw-text-on-status';
// The type colours were seven Tailwind-palette hexes. They carry no status
// meaning beyond "these are different kinds of event", which is what the
// --chart-* ramp is for; the two that DO mean something — a payment landing, and
// the app's own class event — are --success and --primary.
const TOAST_ICON_TONE = {
  class_started: 'tw-bg-primary',
  class_ended: 'tw-bg-chart-1',
  chat_message: 'tw-bg-chart-5',
  whatsapp_message: 'tw-bg-chart-2',
  assignment_posted: 'tw-bg-chart-3',
  assignment_graded: 'tw-bg-success',
  assignment_returned: 'tw-bg-warning',
  batch_update: 'tw-bg-chart-2',
  payment_received: 'tw-bg-success',
  payment_settled: 'tw-bg-success',
  ai_report: 'tw-bg-chart-1',
  enquiry_new: 'tw-bg-success',
  general: 'tw-bg-muted-fg',
};
const TOAST_CONTENT = 'tw-min-w-0 tw-flex-1';
const TOAST_TITLE =
  'tw-mb-0.5 tw-truncate tw-text-[0.84rem] tw-font-medium tw-text-content';
const TOAST_MESSAGE =
  'tw-line-clamp-2 tw-text-[0.78rem] tw-leading-[1.4] tw-text-content-muted';
const TOAST_ACTIONS = 'tw-flex tw-border-0 tw-border-t tw-border-solid tw-border-line-soft';
const TOAST_CTA =
  'tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 ' +
  'tw-border-none tw-bg-transparent tw-px-0 tw-py-2 tw-text-[0.8rem] tw-font-semibold ' +
  'tw-text-primary tw-transition-colors tw-duration-150 hover:tw-bg-surface-hover';
const TOAST_REMIND =
  'tw-flex tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 ' +
  'tw-border-l tw-border-solid tw-border-line-soft tw-bg-transparent tw-text-content-subtle ' +
  'tw-transition-all tw-duration-150 hover:tw-bg-surface-hover hover:tw-text-content-muted';
const TOAST_PROGRESS =
  'tw-absolute tw-bottom-0 tw-left-0 tw-h-0.5 tw-w-full tw-origin-left tw-animate-countdown ' +
  'tw-bg-primary tw-opacity-50';

// ── New-enquiry toast ──
// Shares the bottom-right stack with every other toast, and carries enough to
// act on without opening the record: who, where they came from, the number to
// ring.
const ENQ_TOAST =
  'tw-group tw-pointer-events-auto tw-relative tw-w-[340px] tw-max-w-[calc(100vw-32px)] ' +
  // A fixed width in a stretch container would otherwise leave it short of the
  // right edge that the full-width toast reaches.
  'tw-self-end tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-border-line ' +
  // The one piece of colour: a lead is the only toast the institute earns money
  // from, and the edge is enough to tell it apart at a glance.
  'tw-border-l-[3px] tw-border-l-success ' +
  'tw-bg-surface-raised tw-px-[13px] tw-pb-3.5 tw-pt-3 tw-font-sans tw-shadow-popover';
const ENQ_CLOSE =
  'tw-absolute tw-right-2 tw-top-2 tw-flex tw-h-[22px] tw-w-[22px] tw-cursor-pointer ' +
  'tw-items-center tw-justify-center tw-rounded-[4px] tw-border-none tw-bg-transparent ' +
  'tw-text-content-subtle tw-opacity-0 tw-transition-all tw-duration-150 ' +
  'group-hover:tw-opacity-100 hover:tw-bg-surface-hover hover:tw-text-content';
const ENQ_HEADER = 'tw-flex tw-items-center tw-gap-[9px] tw-pr-[22px]';
const ENQ_AVATAR =
  'tw-grid tw-h-[30px] tw-w-[30px] tw-shrink-0 tw-place-items-center tw-rounded-full ' +
  'tw-bg-success/[0.12] tw-text-success';
const ENQ_META = 'tw-min-w-0';
const ENQ_NAME = 'tw-truncate tw-text-[15px] tw-font-semibold tw-text-content';
const ENQ_SUB = 'tw-flex tw-items-center tw-gap-[5px] tw-text-[11px] tw-text-content-subtle';
const ENQ_SOURCE = 'tw-font-semibold tw-text-success';
const ENQ_DOT = 'tw-opacity-60';
const ENQ_LINES = 'tw-mb-2.5 tw-mt-[9px] tw-flex tw-flex-col tw-gap-[3px]';
const ENQ_LINE =
  'tw-inline-flex tw-items-center tw-gap-[5px] tw-text-[13px] tw-text-content-muted tw-no-underline';
const ENQ_LINE_ICON = 'tw-shrink-0 tw-text-content-subtle';
// The phone number dials. On a laptop the softphone answers it, on a tablet the
// dialler does — either way it is one tap, which is the difference between
// calling back now and calling back tomorrow.
const ENQ_LINE_LINK =
  'tw-inline-flex tw-items-center tw-gap-[5px] tw-text-[13px] tw-font-semibold ' +
  'tw-text-content tw-no-underline hover:tw-text-success hover:tw-underline';
const ENQ_LINE_MUTED =
  'tw-inline-flex tw-items-center tw-gap-[5px] tw-text-xs tw-text-content-subtle tw-no-underline';
const ENQ_CTA =
  'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[5px] tw-rounded-[6px] tw-border-0 ' +
  'tw-bg-success tw-px-[11px] tw-py-[5px] tw-text-[13px] tw-font-semibold tw-text-on-status ' +
  'hover:tw-bg-[color-mix(in_srgb,hsl(var(--success))_82%,black)]';
const ENQ_PROGRESS =
  'tw-absolute tw-bottom-0 tw-left-0 tw-h-0.5 tw-w-full tw-origin-left tw-animate-countdown ' +
  'tw-bg-success tw-opacity-50';

// ── WhatsApp-styled toast ──
// Mimics a WhatsApp incoming message: rounded card + chat bubble with tail.
// Every colour is a --wa-client-* / --ext-whatsapp-* role token, so it follows
// the app's theme while still looking like WhatsApp rather than like us.
const WA_TOAST =
  'tw-group tw-pointer-events-auto tw-relative tw-w-[340px] tw-max-w-[calc(100vw-32px)] ' +
  'tw-self-end tw-overflow-hidden tw-rounded-[14px] tw-border tw-border-solid ' +
  'tw-border-[var(--elevate-2)] tw-bg-[var(--wa-client-card)] tw-font-sans ' +
  'tw-text-[var(--wa-client-ink)] tw-shadow-popover';
const WA_CLOSE =
  'tw-absolute tw-right-2 tw-top-2 tw-z-[3] tw-flex tw-h-6 tw-w-6 tw-cursor-pointer ' +
  'tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent ' +
  'tw-text-[var(--wa-client-meta)] tw-opacity-0 tw-transition-all tw-duration-150 ' +
  'group-hover:tw-opacity-100 hover:tw-bg-[var(--elevate-2)]';
const WA_HEADER = 'tw-flex tw-items-center tw-gap-2.5 tw-pb-2.5 tw-pl-3 tw-pr-[38px] tw-pt-3';
const WA_AVATAR =
  'tw-flex tw-h-[38px] tw-w-[38px] tw-shrink-0 tw-items-center tw-justify-center ' +
  'tw-rounded-full tw-text-[0.82rem] tw-font-medium tw-tracking-[0.5px] tw-text-on-status ' +
  'tw-bg-[linear-gradient(135deg,var(--ext-whatsapp),var(--ext-whatsapp-green-dark))] ' +
  'tw-shadow-card';
const WA_META = 'tw-min-w-0 tw-flex-1';
const WA_NAME =
  'tw-truncate tw-text-[0.92rem] tw-font-semibold tw-leading-[1.2] tw-text-[var(--wa-client-ink)]';
const WA_SUB =
  'tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-[0.7rem] tw-leading-none ' +
  'tw-text-[var(--wa-client-meta)]';
const WA_SUB_ICON = 'tw-text-[var(--ext-whatsapp)]';
const WA_DOT = 'tw-mx-px tw-opacity-60';
const WA_BUBBLE =
  'tw-relative tw-mb-3 tw-ml-14 tw-mr-3.5 tw-mt-1 tw-block tw-max-w-[calc(100%-70px)] ' +
  'tw-cursor-pointer tw-rounded-sm tw-rounded-bl-[2px] tw-border-none ' +
  'tw-bg-[var(--wa-client-out)] tw-px-3 tw-py-2 tw-text-left tw-text-[0.85rem] ' +
  'tw-leading-[1.35] tw-text-[var(--wa-client-ink)] ' +
  'tw-shadow-[var(--wa-client-bubble-shadow)] tw-transition-[filter] tw-duration-150 ' +
  'hover:tw-brightness-[1.03]';
const WA_BUBBLE_TEXT = 'tw-line-clamp-4 tw-whitespace-pre-wrap tw-break-words';
// Speech-bubble tail on the bottom-left of the bubble (incoming).
const WA_BUBBLE_TAIL =
  'tw-absolute tw--left-1.5 tw-bottom-0 tw-h-3 tw-w-3 tw-bg-[var(--wa-client-out)] ' +
  '[clip-path:polygon(100%_0,100%_100%,0_100%)]';
const WA_CTA_ROW =
  'tw-flex tw-border-0 tw-border-t tw-border-solid tw-border-[var(--elevate-2)]';
const WA_CTA =
  'tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 ' +
  'tw-border-none tw-bg-transparent tw-px-0 tw-py-[9px] tw-text-[0.82rem] tw-font-semibold ' +
  'tw-text-[var(--wa-client-action)] tw-transition-colors tw-duration-150 ' +
  'hover:tw-bg-[color-mix(in_srgb,var(--ext-whatsapp)_10%,transparent)]';
const WA_PROGRESS =
  'tw-absolute tw-bottom-0 tw-left-0 tw-h-0.5 tw-w-full tw-origin-left tw-animate-countdown ' +
  'tw-bg-[var(--ext-whatsapp)] tw-opacity-70';

// ── Modal ──
const MODAL_OVERLAY =
  'tw-fixed tw-inset-0 tw-z-critical tw-flex tw-animate-panel-fade tw-items-center ' +
  'tw-justify-center tw-bg-[var(--overlay-bg)] tw-p-5';
const MODAL =
  'tw-relative tw-w-full tw-max-w-[400px] tw-animate-dialog-in tw-rounded-lg tw-border ' +
  'tw-border-solid tw-border-line tw-bg-surface tw-px-7 tw-pb-6 tw-pt-8 tw-text-center ' +
  'tw-shadow-popover max-[480px]:tw-mx-3 max-[480px]:tw-px-5 max-[480px]:tw-pb-5 ' +
  'max-[480px]:tw-pt-6';
const MODAL_CLOSE =
  'tw-absolute tw-right-3 tw-top-3 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center ' +
  'tw-justify-center tw-rounded-sm tw-border-none tw-bg-transparent tw-text-content-subtle ' +
  'tw-transition-all tw-duration-150 hover:tw-bg-surface-hover hover:tw-text-content';
const MODAL_ICON =
  'tw-mx-auto tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center ' +
  'tw-rounded-[14px] tw-bg-primary-solid tw-text-primary-solid-fg';
const MODAL_TITLE = 'tw-mb-2 tw-mt-0 tw-text-[1.1rem] tw-font-medium tw-text-content';
const MODAL_MESSAGE =
  'tw-mb-6 tw-mt-0 tw-text-[0.88rem] tw-leading-[1.5] tw-text-content-muted';
const MODAL_ACTIONS = 'tw-flex tw-justify-center tw-gap-2.5';
const MODAL_CTA =
  'tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-md tw-border-none ' +
  'tw-bg-primary-solid tw-px-6 tw-py-2.5 tw-text-[0.88rem] tw-font-semibold tw-text-primary-solid-fg ' +
  'tw-transition-colors tw-duration-150 ' +
  'hover:tw-bg-[color-mix(in_srgb,hsl(var(--primary-solid))_88%,black)]';
const MODAL_DISMISS =
  'tw-cursor-pointer tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-transparent ' +
  'tw-px-5 tw-py-2.5 tw-text-[0.85rem] tw-font-medium tw-text-content-muted ' +
  'tw-transition-all tw-duration-150 hover:tw-bg-surface-hover hover:tw-text-content';

const ENTER_TOAST = 'tw-animate-toast-slide-in';
const EXIT_TOAST = 'tw-animate-toast-slide-out';


// ── Icon map ──
const ICON_MAP = {
  class_started: Video,
  class_ended: Video,
  chat_message: MessageCircle,
  whatsapp_message: MessageSquare,
  assignment_posted: FileText,
  assignment_graded: Award,
  assignment_returned: RotateCcw,
  batch_update: Layers,
  payment_received: CreditCard,
  payment_settled: CreditCard,
  ai_report: BarChart3,
  enquiry_new: UserPlus,
  general: Bell,
};

// ── CTA map ──
const CTA_MAP = {
  class_started: { label: 'Join Now', icon: Video },
  chat_message: { label: 'Open Chat', icon: MessageCircle },
  whatsapp_message: { label: 'Open Inbox', icon: MessageSquare },
  assignment_posted: { label: 'View Assignment', icon: ArrowRight },
  assignment_graded: { label: 'View Assignment', icon: ArrowRight },
  assignment_returned: { label: 'View Assignment', icon: ArrowRight },
  batch_update: { label: 'View Details', icon: ArrowRight },
  payment_received: { label: 'View Receipt', icon: ArrowRight },
  ai_report: { label: 'View Report', icon: BarChart3 },
  enquiry_new: { label: 'Open Enquiry', icon: ArrowRight },
};

// ── Display mode for each type ──
const DISPLAY_MODE = {
  class_started: 'banner',
  class_ended: 'toast',
  chat_message: 'toast',
  whatsapp_message: 'toast',
  assignment_posted: 'toast',
  assignment_graded: 'toast',
  assignment_returned: 'toast',
  batch_update: 'toast',
  payment_received: 'toast',
  ai_report: 'toast',
  enquiry_new: 'toast',
  general: 'toast',
};

/**
 * Toasts that survive the stack cap when something newer arrives.
 *
 * Everything shares the one bottom-right stack, so a busy WhatsApp afternoon
 * would otherwise push a lead off the screen before anyone looked up — and a
 * lead is the one alert whose value decays in minutes. Overflow therefore
 * drops the ordinary toasts first and only evicts one of these when the whole
 * stack is leads.
 */
const PRIORITY_TYPES = new Set(['enquiry_new']);

const TOAST_DURATION = 8000;
/** Longer than a chat toast: this one is a decision, not an FYI. */
const ENQUIRY_TOAST_DURATION = 14000;
const BANNER_DURATION = 15000;
const MAX_TOASTS = 3;

function getRoutePrefix() {
  const user = getCurrentUser();
  if (user?.usertype === 'student') return '/s';
  if (user?.usertype === 'instructor') return '/i';
  return '';
}

let idCounter = 0;

const InAppNotification = () => {
  const [toasts, setToasts] = useState([]);
  const [banner, setBanner] = useState(null);
  const [modal, setModal] = useState(null);
  const timersRef = useRef(new Map());
  const navigate = useNavigate();

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    clearTimer(id);
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    setBanner((prev) => (prev?.id === id ? { ...prev, exiting: true } : prev));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setBanner((prev) => (prev?.id === id ? null : prev));
    }, 300);
    trackNotification(id, 'dismissed');
  }, [clearTimer]);

  // ── Auto-dismiss timers ──
  const startTimer = useCallback((id, duration) => {
    const timer = setTimeout(() => {
      dismissNotification(id);
    }, duration);
    timersRef.current.set(id, timer);
  }, [dismissNotification]);

  // ── Add notification ──
  const addNotification = useCallback((notification) => {
    const type = notification.type || 'general';

    // The user's preferences, the dedup window and the rate limit all live in
    // notificationManager, and BOTH ways in here go through it: the socket
    // event, and the same event again as a push the service worker forwards.
    // Neither used to consult it, so a chat message with the tab focused
    // raised two toasts and a muted category raised one anyway.
    if (!shouldShowInApp({ ...notification, type }).show) return null;

    const id = `inapp-${++idCounter}`;
    const mode = notification.displayMode || DISPLAY_MODE[type] || 'toast';
    const item = {
      ...notification,
      id,
      type,
      mode,
      exiting: false,
      createdAt: Date.now(),
    };

    if (mode === 'modal') {
      setModal(item);
    } else if (mode === 'banner') {
      setBanner((prev) => {
        if (prev) clearTimer(prev.id);
        return item;
      });
      startTimer(id, BANNER_DURATION);
    } else {
      setToasts((prev) => {
        const updated = [...prev, item];
        if (updated.length <= MAX_TOASTS) return updated;
        // Oldest-first overflow, but ordinary toasts go before a lead of the
        // same age — three WhatsApp messages must not silently evict the one
        // notification someone is expected to act on.
        const overflow = updated.length - MAX_TOASTS;
        const ordinary = updated.filter((t) => !PRIORITY_TYPES.has(t.type));
        const pool = ordinary.length >= overflow ? ordinary : updated;
        const evicted = new Set(pool.slice(0, overflow));
        evicted.forEach((t) => clearTimer(t.id));
        return updated.filter((t) => !evicted.has(t));
      });
      startTimer(id, PRIORITY_TYPES.has(type) ? ENQUIRY_TOAST_DURATION : TOAST_DURATION);
    }

    trackNotification(id, 'displayed', { type, mode });
    return id;
  }, [clearTimer, startTimer]);

  // ── Listen for notification events ──
  useEffect(() => {
    const handler = (e) => addNotification(e.detail);
    window.addEventListener('vc-notification', handler);
    return () => window.removeEventListener('vc-notification', handler);
  }, [addNotification]);

  // ── Listen for SW forwarded push notifications ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event) => {
      if (event.data?.type === 'PUSH_NOTIFICATION') {
        const payload = event.data.payload;
        const notifType = payload.data?.type || 'general';

        // Same backstop as SocketContext: staff-only pushes stay with staff.
        if (isStaffOnlyNotification(notifType) && !isStaffUser(getCurrentUser()?.usertype)) return;

        // Already looking at the page this is about: the inbox shows the
        // message itself, so a toast on top of it is noise.
        const path = window.location.pathname;
        const hash = window.location.hash;
        const isOnWhatsApp =
          (path.includes('/whatsapp') && hash === '#inbox') ||
          path.includes('/whatsapp-inbox');
        if (notifType === 'whatsapp_message' && isOnWhatsApp) {
          playNotificationSound();
          return;
        }
        if (notifType === 'chat_message' && path.includes('/chat')) return;

        // Deduplication now lives in addNotification, shared with the socket
        // path — the two carry different tags for one event, so a dedup local
        // to this handler could never have caught the pair.
        const shown = addNotification({
          type: notifType,
          title: payload.title,
          message: payload.body,
          data: payload.data,
          batchId: payload.data?.batchId,
          tag: payload.tag,
        });
        // Sound belongs to whichever path actually rendered it: when the socket
        // got here first this push is a duplicate and stays silent.
        if (shown) playNotificationSound();
      }
      if (event.data?.type === 'NOTIFICATION_CLICKED') {
        trackNotification(event.data.payload?.tag || 'unknown', 'clicked');
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [addNotification]);

  // ── Navigation handler ──
  const handleAction = useCallback((item) => {
    dismissNotification(item.id);
    trackNotification(item.id, 'clicked', { type: item.type });

    const prefix = getRoutePrefix();
    const url = item.data?.url || item.link;

    if (url) {
      navigate(url);
      return;
    }

    // Fallback deep links by type
    switch (item.type) {
      case 'class_started':
        if (item.batchId) navigate(`${prefix}/classroom/${item.batchId}`);
        break;
      case 'chat_message':
        navigate(`${prefix}/chat`);
        break;
      case 'assignment_posted':
        if (item.batchId) navigate(`${prefix}/assignments`);
        break;
      default:
        break;
    }
  }, [dismissNotification, navigate]);

  const handleRemindLater = useCallback((item) => {
    dismissNotification(item.id);
    remindMeLater(item, 5 * 60 * 1000);
  }, [dismissNotification]);

  // ── Cleanup all timers on unmount ──
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);


  return (
    <>
      {/* ── Top Banner (class started, critical alerts) ── */}
      {banner && (() => {
        const brand = isBrandBanner(banner.type);
        return (
          <div className={`${brand ? BANNER_BRAND : BANNER_NEUTRAL} ${banner.exiting ? 'tw-animate-banner-up' : 'tw-animate-banner-down'}`}>
            <div className={BANNER_CONTENT}>
              <div className={brand ? BANNER_ICON_BRAND : BANNER_ICON_NEUTRAL}>
                {React.createElement(ICON_MAP[banner.type] || Bell, { size: 20 })}
              </div>
              <div className={BANNER_TEXT}>
                <span className={BANNER_TITLE}>{banner.title}</span>
                <span className={BANNER_MESSAGE}>{banner.message}</span>
              </div>
            </div>
            <div className={BANNER_ACTIONS}>
              {CTA_MAP[banner.type] && (
                <button
                  className={brand ? BANNER_CTA_BRAND : BANNER_CTA_NEUTRAL}
                  onClick={() => handleAction(banner)}
                >
                  {React.createElement(CTA_MAP[banner.type].icon, { size: 15 })}
                  <span>{CTA_MAP[banner.type].label}</span>
                </button>
              )}
              {banner.type === 'class_started' && (
                <button
                  className={brand ? BANNER_REMIND_BRAND : BANNER_REMIND_NEUTRAL}
                  onClick={() => handleRemindLater(banner)}
                >
                  <Clock size={14} />
                  <span className={BANNER_REMIND_LABEL}>Remind Later</span>
                </button>
              )}
              <button
                className={brand ? BANNER_CLOSE_BRAND : BANNER_CLOSE_NEUTRAL}
                onClick={() => dismissNotification(banner.id)}
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Toast stack (bottom-right) ── */}
      {toasts.length > 0 && (
        <div className={TOAST_CONTAINER}>
          {toasts.map((toast) => {
            const motion = toast.exiting ? EXIT_TOAST : ENTER_TOAST;

            /**
             * A new enquiry, with enough on it to decide without opening
             * anything: who, where they came from, and the number to ring.
             * Tapping the number dials rather than navigating — on a phone that
             * is the whole action, and on a desktop softphone it still is.
             */
            if (toast.type === 'enquiry_new') {
              const meta = toast.data || {};
              const name = meta.name || toast.title || 'New enquiry';
              const time = new Date(toast.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

              return (
                <div key={toast.id} className={`${ENQ_TOAST} ${motion}`} role="alert">
                  <button
                    className={ENQ_CLOSE}
                    onClick={() => dismissNotification(toast.id)}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>

                  <div className={ENQ_HEADER}>
                    <div className={ENQ_AVATAR}><UserPlus size={15} /></div>
                    <div className={ENQ_META}>
                      <div className={ENQ_NAME}>{name}</div>
                      <div className={ENQ_SUB}>
                        <span className={ENQ_SOURCE}>{meta.source || 'New enquiry'}</span>
                        <span className={ENQ_DOT}>•</span>
                        <span>{time}</span>
                      </div>
                    </div>
                  </div>

                  {(meta.mobile || meta.email || meta.campaignName) && (
                    <div className={ENQ_LINES}>
                      {meta.mobile && (
                        <a className={ENQ_LINE_LINK} href={`tel:${meta.mobile}`}>
                          <Phone size={11} className={ENQ_LINE_ICON} /> {meta.mobile}
                        </a>
                      )}
                      {meta.email && (
                        <span className={ENQ_LINE}><Mail size={11} className={ENQ_LINE_ICON} /> {meta.email}</span>
                      )}
                      {meta.campaignName && (
                        <span className={ENQ_LINE_MUTED}>
                          <BarChart3 size={11} className={ENQ_LINE_ICON} /> {meta.campaignName}
                        </span>
                      )}
                    </div>
                  )}

                  <button className={ENQ_CTA} onClick={() => handleAction(toast)}>
                    <ArrowRight size={13} />
                    <span>Open enquiry</span>
                  </button>

                  <div
                    className={ENQ_PROGRESS}
                    style={{ animationDuration: `${ENQUIRY_TOAST_DURATION}ms` }}
                  />
                </div>
              );
            }

            // ── WhatsApp-styled toast: chat-bubble look (light/dark themed) ──
            if (toast.type === 'whatsapp_message') {
              const phone = toast.data?.phone || '';
              const contactName = toast.data?.contactName || toast.title?.replace(/^WhatsApp:\s*/i, '') || phone || 'WhatsApp';
              const initials = (contactName || '?').trim().split(/\s+/).slice(0, 2)
                .map((p) => p[0]?.toUpperCase() || '').join('') || 'WA';
              const time = new Date(toast.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={toast.id} className={`${WA_TOAST} ${motion}`} role="alert">
                  <button
                    className={WA_CLOSE}
                    onClick={() => dismissNotification(toast.id)}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                  <div className={WA_HEADER}>
                    <div className={WA_AVATAR}>{initials}</div>
                    <div className={WA_META}>
                      <div className={WA_NAME}>{contactName}</div>
                      <div className={WA_SUB}>
                        <MessageSquare size={11} className={WA_SUB_ICON} />
                        <span>WhatsApp</span>
                        <span className={WA_DOT}>•</span>
                        <span>{time}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={WA_BUBBLE}
                    onClick={() => handleAction(toast)}
                    aria-label="Open WhatsApp inbox"
                  >
                    <span className={WA_BUBBLE_TEXT}>{toast.message || 'New message'}</span>
                    <span className={WA_BUBBLE_TAIL} aria-hidden="true" />
                  </button>
                  <div className={WA_CTA_ROW}>
                    <button className={WA_CTA} onClick={() => handleAction(toast)}>
                      <Reply size={13} />
                      <span>Open Inbox</span>
                    </button>
                  </div>
                  <div
                    className={WA_PROGRESS}
                    style={{ animationDuration: `${TOAST_DURATION}ms` }}
                  />
                </div>
              );
            }

            // ── Default toast layout ──
            const IconComp = ICON_MAP[toast.type] || Bell;
            const cta = CTA_MAP[toast.type];
            return (
              <div key={toast.id} className={`${TOAST} ${motion}`}>
                <button
                  className={TOAST_CLOSE}
                  onClick={() => dismissNotification(toast.id)}
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
                <div className={TOAST_BODY}>
                  <div className={`${TOAST_ICON} ${TOAST_ICON_TONE[toast.type] || TOAST_ICON_TONE.general}`}>
                    <IconComp size={18} />
                  </div>
                  <div className={TOAST_CONTENT}>
                    <div className={TOAST_TITLE}>{toast.title}</div>
                    <div className={TOAST_MESSAGE}>{toast.message}</div>
                  </div>
                </div>
                {cta && (
                  <div className={TOAST_ACTIONS}>
                    <button className={TOAST_CTA} onClick={() => handleAction(toast)}>
                      {React.createElement(cta.icon, { size: 14 })}
                      <span>{cta.label}</span>
                    </button>
                    <button className={TOAST_REMIND} onClick={() => handleRemindLater(toast)}>
                      <Clock size={13} />
                    </button>
                  </div>
                )}
                <div
                  className={TOAST_PROGRESS}
                  style={{ animationDuration: `${TOAST_DURATION}ms` }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal (critical/blocking) ── */}
      {modal && (
        <div className={MODAL_OVERLAY} onClick={() => dismissNotification(modal.id)}>
          <div className={MODAL} onClick={(e) => e.stopPropagation()}>
            <button
              className={MODAL_CLOSE}
              onClick={() => dismissNotification(modal.id)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className={MODAL_ICON}>
              {React.createElement(ICON_MAP[modal.type] || Bell, { size: 32 })}
            </div>
            <h3 className={MODAL_TITLE}>{modal.title}</h3>
            <p className={MODAL_MESSAGE}>{modal.message}</p>
            <div className={MODAL_ACTIONS}>
              {CTA_MAP[modal.type] && (
                <button className={MODAL_CTA} onClick={() => handleAction(modal)}>
                  {React.createElement(CTA_MAP[modal.type].icon, { size: 16 })}
                  <span>{CTA_MAP[modal.type].label}</span>
                </button>
              )}
              <button className={MODAL_DISMISS} onClick={() => dismissNotification(modal.id)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InAppNotification;
